import { SkillProgress, PlayerProfile, GameRequest, GameMessage } from '../types/skillProgress';
import { samplePlayers } from '../data/samplePlayers';

class SkillProgressService {
  private readonly STORAGE_KEYS = {
    skillProgress: 'mysportscoach_skill_progress',
    playerProfiles: 'mysportscoach_player_profiles',
    gameRequests: 'mysportscoach_game_requests',
    gameMessages: 'mysportscoach_game_messages'
  };

  // Skill Progress Management
  async saveSkillProgress(userId: string, sportId: string, skillId: string, progress: Partial<SkillProgress>): Promise<void> {
    const key = `${userId}_${sportId}_${skillId}`;
    const skillProgress: SkillProgress = {
      userId,
      sportId,
      skillId,
      status: progress.status || 'not-started',
      lastPracticed: progress.lastPracticed || new Date().toISOString(),
      notes: progress.notes,
      rating: progress.rating,
      completedAt: progress.completedAt
    };

    const allProgress = this.getStoredData<{ [key: string]: SkillProgress }>(this.STORAGE_KEYS.skillProgress);
    allProgress[key] = skillProgress;
    this.setStoredData(this.STORAGE_KEYS.skillProgress, allProgress);
  }

  async getSkillProgress(userId: string, sportId: string): Promise<SkillProgress[]> {
    const allProgress = this.getStoredData<{ [key: string]: SkillProgress }>(this.STORAGE_KEYS.skillProgress);
    return Object.values(allProgress).filter(progress => 
      progress.userId === userId && progress.sportId === sportId
    );
  }

  async getSkillProgressForSkill(userId: string, sportId: string, skillId: string): Promise<SkillProgress | null> {
    const key = `${userId}_${sportId}_${skillId}`;
    const allProgress = this.getStoredData<{ [key: string]: SkillProgress }>(this.STORAGE_KEYS.skillProgress);
    return allProgress[key] || null;
  }

  async markSkillCompleted(userId: string, sportId: string, skillId: string, rating?: number, notes?: string): Promise<void> {
    await this.saveSkillProgress(userId, sportId, skillId, {
      status: 'mastered',
      completedAt: new Date().toISOString(),
      rating,
      notes
    });
  }

  async updateSkillStatus(userId: string, sportId: string, skillId: string, status: SkillProgress['status']): Promise<void> {
    await this.saveSkillProgress(userId, sportId, skillId, { status });
  }

  // Player Profile Management
  async createPlayerProfile(profile: Omit<PlayerProfile, 'userId'>): Promise<string> {
    const userId = `player_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const playerProfile: PlayerProfile = {
      userId,
      ...profile,
      isActive: true,
      lastActive: new Date().toISOString()
    };

    const profiles = this.getStoredData<{ [userId: string]: PlayerProfile }>(this.STORAGE_KEYS.playerProfiles);
    profiles[userId] = playerProfile;
    this.setStoredData(this.STORAGE_KEYS.playerProfiles, profiles);

    return userId;
  }

  async updatePlayerProfile(userId: string, updates: Partial<PlayerProfile>): Promise<void> {
    const profiles = this.getStoredData<{ [userId: string]: PlayerProfile }>(this.STORAGE_KEYS.playerProfiles);
    if (profiles[userId]) {
      profiles[userId] = {
        ...profiles[userId],
        ...updates,
        lastActive: new Date().toISOString()
      };
      this.setStoredData(this.STORAGE_KEYS.playerProfiles, profiles);
    }
  }

  async getPlayerProfile(userId: string): Promise<PlayerProfile | null> {
    const profiles = this.getStoredData<{ [userId: string]: PlayerProfile }>(this.STORAGE_KEYS.playerProfiles);
    return profiles[userId] || null;
  }

  async searchPlayers(filters: {
    sport?: string;
    skillLevel?: string;
    location?: string;
    club?: string;
    maxDistance?: number;
    excludeUserId?: string;
  }): Promise<PlayerProfile[]> {
    const profiles = this.getStoredData<{ [userId: string]: PlayerProfile }>(this.STORAGE_KEYS.playerProfiles);
    
    // Initialize with sample data if no profiles exist
    if (Object.keys(profiles).length === 0) {
      const sampleProfiles: { [userId: string]: PlayerProfile } = {};
      samplePlayers.forEach(player => {
        sampleProfiles[player.userId] = player;
      });
      this.setStoredData(this.STORAGE_KEYS.playerProfiles, sampleProfiles);
    }
    
    const allProfiles = this.getStoredData<{ [userId: string]: PlayerProfile }>(this.STORAGE_KEYS.playerProfiles);
    
    return Object.values(allProfiles).filter(profile => {
      if (filters.excludeUserId && profile.userId === filters.excludeUserId) return false;
      if (filters.skillLevel && profile.skillLevel !== filters.skillLevel) return false;
      if (filters.club && profile.club !== filters.club) return false;
      if (filters.location && !profile.location.toLowerCase().includes(filters.location.toLowerCase())) return false;
      if (!profile.isActive) return false;
      
      return true;
    });
  }

  // Game Request Management
  async createGameRequest(request: Omit<GameRequest, 'id' | 'createdAt' | 'status'>): Promise<string> {
    const gameRequest: GameRequest = {
      id: `game_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...request,
      status: 'pending',
      createdAt: new Date().toISOString()
    };

    const requests = this.getStoredData<GameRequest[]>(this.STORAGE_KEYS.gameRequests);
    requests.push(gameRequest);
    this.setStoredData(this.STORAGE_KEYS.gameRequests, requests);

    return gameRequest.id;
  }

  async getGameRequests(userId: string): Promise<GameRequest[]> {
    const requests = this.getStoredData<GameRequest[]>(this.STORAGE_KEYS.gameRequests);
    return requests.filter(request => 
      request.requesterId === userId || request.targetPlayerId === userId
    );
  }

  async updateGameRequestStatus(requestId: string, status: GameRequest['status']): Promise<void> {
    const requests = this.getStoredData<GameRequest[]>(this.STORAGE_KEYS.gameRequests);
    const requestIndex = requests.findIndex(r => r.id === requestId);
    
    if (requestIndex !== -1) {
      requests[requestIndex].status = status;
      requests[requestIndex].respondedAt = new Date().toISOString();
      this.setStoredData(this.STORAGE_KEYS.gameRequests, requests);
    }
  }

  // Game Messages Management
  async sendGameMessage(message: Omit<GameMessage, 'id' | 'timestamp' | 'isRead'>): Promise<string> {
    const gameMessage: GameMessage = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...message,
      timestamp: new Date().toISOString(),
      isRead: false
    };

    const messages = this.getStoredData<GameMessage[]>(this.STORAGE_KEYS.gameMessages);
    messages.push(gameMessage);
    this.setStoredData(this.STORAGE_KEYS.gameMessages, messages);

    return gameMessage.id;
  }

  async getGameMessages(gameRequestId: string): Promise<GameMessage[]> {
    const messages = this.getStoredData<GameMessage[]>(this.STORAGE_KEYS.gameMessages);
    return messages
      .filter(msg => msg.gameRequestId === gameRequestId)
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }

  async markMessageAsRead(messageId: string): Promise<void> {
    const messages = this.getStoredData<GameMessage[]>(this.STORAGE_KEYS.gameMessages);
    const messageIndex = messages.findIndex(m => m.id === messageId);
    
    if (messageIndex !== -1) {
      messages[messageIndex].isRead = true;
      this.setStoredData(this.STORAGE_KEYS.gameMessages, messages);
    }
  }

  // Helper methods for localStorage
  private getStoredData<T>(key: string): T {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : (Array.isArray(new T()) ? [] : {});
    } catch (error) {
      console.error(`Error reading ${key} from localStorage:`, error);
      return Array.isArray(new T()) ? [] : {} as T;
    }
  }

  private setStoredData<T>(key: string, data: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error(`Error writing ${key} to localStorage:`, error);
    }
  }
}

export const skillProgressService = new SkillProgressService();
export default skillProgressService;
